import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, BookLock, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) { toast.success("Welcome back"); navigate("/"); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-900">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -left-16 bottom-10 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="flex items-center gap-3 relative z-10">
          <div className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center font-display font-extrabold text-xl text-primary-foreground">B</div>
          <div>
            <div className="font-display font-bold text-white text-lg leading-tight">Project GPI</div>
            <div className="text-xs text-slate-400 tracking-widest">CENTRAL INFORMATION SYSTEM</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl font-bold text-white leading-tight">Every project detail, in one searchable place.</h1>
          <p className="text-slate-400 mt-4 text-base leading-relaxed">Contacts, documents, vendors, machines, rates and follow-ups — organised, linked to projects, and available in seconds.</p>
        </div>
        <div className="text-slate-500 text-xs relative z-10">Secure • Role-based • Mobile-friendly</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div className="lg:hidden flex items-center gap-2 mb-2">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center font-display font-extrabold text-primary-foreground">B</div>
            <span className="font-display font-bold text-lg">Project GPI</span>
          </div>
          <div>
            <div className="inline-flex items-center gap-2 text-primary mb-2"><BookLock className="h-5 w-5" /><span className="overline">Sign in</span></div>
            <h2 className="font-display text-2xl font-bold">Welcome back</h2>
            <p className="text-muted-foreground text-sm mt-1">Enter your credentials to access the CRM book.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="overline text-muted-foreground">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="overline text-muted-foreground">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" data-testid="login-password" />
            </div>
          </div>
          <Button type="submit" className="w-full active:scale-95 transition-transform" disabled={loading} data-testid="login-submit">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Sign In
          </Button>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center"><span className="bg-background px-3 text-[11px] uppercase tracking-wider text-muted-foreground">or</span></div>
          </div>
          <Button type="button" variant="outline" onClick={loginWithGoogle}
            className="w-full gap-2 active:scale-95 transition-transform" data-testid="login-google-btn">
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center"><span className="bg-background px-3 text-[11px] uppercase tracking-wider text-muted-foreground">Gate / Security staff</span></div>
          </div>
          <a href="/gpi/security" data-testid="login-public-portal-link" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-md border-2 border-primary/30 text-primary font-medium text-sm hover:bg-primary/5 transition-colors">
            <ShieldCheck className="h-4 w-4" />Open Security Entry & Exit Portal
          </a>
          <p className="text-center text-[11px] text-muted-foreground">Record gate entries & exits without logging in.</p>
        </form>
      </div>
    </div>
  );
}
